<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootInput from 'dashboard/components/widgets/forms/Input.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      l1_label: '',
      l2_label: '',
      l3_label: '',
    }),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'validation-error']);

const { t } = useI18n();

// 标签层级配置
const LABEL_LEVELS = 3;
const LABEL_FIELDS = Array.from({ length: LABEL_LEVELS }, (_, i) => `l${i + 1}_label`);

// 从store获取所有标签
const allLabels = useMapGetter('labels/getLabels');

// 响应式数据 - 存储用户输入的原始值
const labelValues = ref({
  l1_label: props.modelValue.l1_label || '',
  l2_label: props.modelValue.l2_label || '',
  l3_label: props.modelValue.l3_label || '',
});

// 显示用的标签值 - 直接使用用户输入的值
const displayLabelValues = computed(() => {
  const display = {};
  LABEL_FIELDS.forEach(field => {
    display[field] = labelValues.value[field] || '';
  });
  return display;
});

const dropdownStates = ref({
  l1_label: false,
  l2_label: false,
  l3_label: false,
});

const inputRefs = ref({
  l1_label: null,
  l2_label: null,
  l3_label: null,
});

// 将扁平数组转换为树形结构
const labelTree = computed(() => {
  const tree = {};
  
  allLabels.value.forEach(label => {
    const l1 = label.l1_label;
    const l2 = label.l2_label;
    const l3 = label.l3_label;
    
    if (!l1) return; // 跳过没有一级标签的数据
    
    // 确保键是下划线格式，用于路径匹配
    // 将多个连续空格替换为单个下划线，与用户输入处理保持一致
    const l1Key = l1.replace(/\s+/g, '_');
    const l2Key = l2 ? l2.replace(/\s+/g, '_') : null;
    const l3Key = l3 ? l3.replace(/\s+/g, '_') : null;
    
    if (!tree[l1Key]) {
      tree[l1Key] = {
        label: l1.replace(/_/g, ' '),
        value: l1,
        color: label.color,
        children: {}
      };
    }
    
    if (l2 && !tree[l1Key].children[l2Key]) {
      tree[l1Key].children[l2Key] = {
        // label: l2.replace(/_/g, ' '),
        label: l2,
        value: l2,
        color: label.color,
        children: {}
      };
    }
    
    if (l3 && l2 && !tree[l1Key].children[l2Key].children[l3Key]) {
      tree[l1Key].children[l2Key].children[l3Key] = {
        // label: l3.replace(/_/g, ' '),
        label: l3,
        value: l3,
        color: label.color
      };
    }
  });
  
  return tree;
});

const getLabelOptions = (level) => {
  const fieldName = `l${level}_label`;
  const inputValue = labelValues.value[fieldName] || '';
  
  // 获取当前层级的选项
  let options = [];
  
  if (level === 1) {
    // 一级标签：显示所有一级标签
    options = Object.values(labelTree.value);
  } else {
    // 二级和三级标签：需要检查上级路径
    const parentField = `l${level - 1}_label`;
    const parentValue = labelValues.value[parentField];
    
    if (!parentValue) {
      // 上级标签没有值，不显示任何选项
      return [];
    }
    
    // 获取上级路径的节点
    let currentNode = labelTree.value;
    
    // 遍历所有上级路径
    for (let i = 0; i < level - 1; i++) {
      const currentParentField = `l${i + 1}_label`;
      const currentParentValue = labelValues.value[currentParentField];
      
      if (!currentParentValue) {
        return []; // 上级标签没有值
      }
      
      // 将用户输入的值转换为下划线格式进行路径匹配
      const normalizedParentValue = currentParentValue.replace(/\s+/g, '_');
      if (!currentNode[normalizedParentValue]) {
        return []; // 路径不存在或不匹配
      }
      
      currentNode = currentNode[normalizedParentValue].children;
    }
    
    // 获取当前层级的选项
    options = Object.values(currentNode);
  }
  
  // 根据输入值过滤选项
  if (!inputValue) {
    return options;
  }
  
  return options.filter(option => 
    option.label.toLowerCase().includes(inputValue.toLowerCase())
  );
};

// 获取标签选项的计算属性
const labelOptions = computed(() => {
  const options = {};
  LABEL_FIELDS.forEach((field, index) => {
    const level = index + 1;
    options[field] = getLabelOptions(level);
  });
  return options;
});

// 标签配置生成函数
const createLabelConfig = (level) => ({
  field: `l${level}_label`,
  label: t(`LABEL_MGMT.FORM.SEARCH_INPUT.L${level}_LABEL`),
  placeholder: t(`LABEL_MGMT.FORM.SEARCH_INPUT.L${level}_PLACEHOLDER`),
  level,
  parentPath: LABEL_FIELDS.slice(0, level - 1).map(field => labelValues.value[field])
});

const labelConfigs = computed(() => 
  Array.from({ length: LABEL_LEVELS }, (_, i) => createLabelConfig(i + 1))
);

const ruleDefaults = {
  required,
  minLength: minLength(1),
  startsWithAlphanumeric: (value) => {
    if (!value) return true;
    return /^[a-zA-Z0-9]/.test(value);
  },
  validLabelCharacters: (value) => {
    if (!value) return true;
    const labelNameReg = /[^a-zA-Z0-9_.\s]/g;
    return !labelNameReg.test(value);
  },
};

// 校验规则
const rules = computed(() => {
  const ruleSet = {};
  LABEL_FIELDS.forEach(field => {
    ruleSet[field] = ruleDefaults;
  });
  return ruleSet;
});

// 使用 vuelidate
const v$ = useVuelidate(rules, labelValues);

// 获取错误信息
const getLabelNameErrorMessage = (field) => {
  const validation = v$.value[field];
  if (!validation.$error) return '';

  let errorMessage = '';
  if (validation.required && validation.required.$invalid) {
    errorMessage = t('LABEL_MGMT.FORM.SEARCH_INPUT.REQUIRED_ERROR');
  } else if (validation.minLength && validation.minLength.$invalid) {
    errorMessage = t('LABEL_MGMT.FORM.SEARCH_INPUT.MINIMUM_LENGTH_ERROR');
  } else if (validation.startsWithAlphanumeric && validation.startsWithAlphanumeric.$invalid) {
    errorMessage = t('LABEL_MGMT.FORM.SEARCH_INPUT.STARTS_WITH_ALPHANUMERIC_ERROR');
  } else if (validation.validLabelCharacters && validation.validLabelCharacters.$invalid) {
    errorMessage = t('LABEL_MGMT.FORM.SEARCH_INPUT.VALID_ERROR');
  }

  return errorMessage ? `${labelConfigs.value.find(config => config.field === field)?.label}: ${errorMessage}` : '';
};

// 校验函数
const validate = async () => {
  const result = await v$.value.$validate();
  if (!result) {
    const errorMessage = LABEL_FIELDS
      .map(field => getLabelNameErrorMessage(field))
      .find(error => error);
    
    if (errorMessage) {
      useAlert(errorMessage);
      emit('validation-error', errorMessage);
    }
  }
  return result;
};

// 暴露校验函数
defineExpose({ validate });

// 通用处理函数
const handleLabelSelect = (field, option) => {
  // 使用 value 保持原始格式，用于路径匹配
  labelValues.value[field] = option.value;
  dropdownStates.value[field] = false;
  
  emitUpdate();
};

const handleLabelInput = (field, value) => {
  // 直接存储用户输入的值，不做任何转换
  labelValues.value[field] = value;
  
  // 检查是否有匹配的选项
  const hasMatchingOptions = labelOptions.value[field].length > 0;
  
  // 当输入框有值且有匹配选项时，或者输入框为空但有选项时，都显示下拉框
  if ((value && value.length > 0 && hasMatchingOptions) || (!value && hasMatchingOptions)) {
    dropdownStates.value[field] = true;
  } else {
    dropdownStates.value[field] = false;
  }
  
  emitUpdate();
};

const handleLabelClick = (field) => {
  // 关闭其他下拉框
  LABEL_FIELDS.forEach(otherField => {
    if (otherField !== field) {
      dropdownStates.value[otherField] = false;
    }
  });
  // 打开当前下拉框
  dropdownStates.value[field] = true;
};

// 处理点击外部关闭下拉框
const handleClickOutside = () => {
  setTimeout(() => {
    LABEL_FIELDS.forEach(field => {
      dropdownStates.value[field] = false;
    });
  }, 100);
};

// 发出更新事件
const emitUpdate = () => {
  // 直接提交用户输入的值
  const formattedValues = {};
  LABEL_FIELDS.forEach(field => {
    formattedValues[field] = labelValues.value[field] || '';
  });
  emit('update:modelValue', formattedValues);
};

// 监听props变化，支持回显
watch(() => props.modelValue, (newValue) => {
  LABEL_FIELDS.forEach(field => {
    // 直接使用外部传入的值
    labelValues.value[field] = newValue[field] || '';
  });
}, { deep: true });

// 组件挂载时初始化
onMounted(() => {
  LABEL_FIELDS.forEach(field => {
    if (props.modelValue[field]) {
      // 直接使用外部传入的值
      labelValues.value[field] = props.modelValue[field];
    }
  });
});
</script>

<template>
  <OnClickOutside class="w-full" @trigger="handleClickOutside">
    <div class="w-full">
      <!-- 标签输入框 -->
      <div
        v-for="(config, index) in labelConfigs"
        :key="config.field"
        :class="[
          'relative mb-3',
          index > 0 ? 'ml-[12px] border-l border-[#eee] pl-[12px]' : ''
        ]"
      >
        <woot-input
          :ref="`inputRefs.${config.field}`"
          :model-value="displayLabelValues[config.field]"
          class="w-full"
          :label="`* ${config.label}`"
          :placeholder="config.placeholder"
          :disabled="disabled"
          @input="(value) => handleLabelInput(config.field, value)"
          @click="() => handleLabelClick(config.field)"
        />
        
        <!-- 下拉框 -->
        <div
          v-show="dropdownStates[config.field] && labelOptions[config.field].length > 0"
          :class="[
            'absolute z-50 w-full bg-white border rounded-md shadow-lg dark:bg-slate-800 border-n-strong overflow-auto',
            index === 0 ? 'max-h-[200px]' : 'max-h-60'
          ]"
          style="top: 100%; margin-top: 0;"
        >
          <ul class="py-1">
            <li
              v-for="option in labelOptions[config.field]"
              :key="option.value"
              class="px-3 py-2 text-sm cursor-pointer hover:bg-n-alpha-2 text-n-slate-12"
              @click="() => handleLabelSelect(config.field, option)"
            >
              <span class="whitespace-pre-wrap">{{ option.label }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </OnClickOutside>
</template>
